import {
  BarChartOutlined,
  DatabaseOutlined,
  DollarOutlined,
  PieChartOutlined,
  TableOutlined,
  UserSwitchOutlined,
} from "@ant-design/icons";
import { DatePicker, Flex, Skeleton, Table } from "antd";
import moment from "moment";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { setQuery } from "../../store/filters";
import BankBarChart from "../../Reuseable/BankBarChart/BankBarChart";
import CheckboxFilterDropdown from "../../Reuseable/CheckboxFilterDropdown/CheckboxFilterDropdown";
import DateFilter from "../../Reuseable/DateFilter/DateFilter";
import BarChartSkeleton from "../../Reuseable/Skeleton/BarchartSkeleton";
import { fetchAgencyProducts } from "../../store/agencySlice";
import {
  fetchBucketWiseAllocation,
  fetchBucketWiseStatus,
} from "../../store/bucketSlice";
import {
  fetchBankOverview,
  fetchCaseIntencity,
  fetchMonthlyPtp,
  fetchWeeklyPaid,
  fetchCaseTrend,
  fetchAllocationBreakdown,
} from "../../store/portfolioSlice";
import NormalButton from "../../Reuseable/NormalButton/NormalButton";
import { ReactComponent as ExportICon } from "../../assets/Icons/exportIcon.svg";
import { exportBucketAllocationFile } from "../../assets/api/othres";

import KpiCard from "../../Pages/DashboardPage/components/KpiCard";
import PanelCard from "../../Pages/DashboardPage/components/PanelCard";
import "./BankDashboardComp.scss";
import dayjs from "dayjs";

import BucketStatusTable from "./BucketStatusTable/BucketStatusTable";
import BucketwiseTable from "./BucketwiseTable/BucketwiseTable";
import AllocationBreakdownOverview from "./AllocationBreakdownOverview/AllocationBreakdownOverview";
import {
  fetchTelecallerBankreportStats,
  fetchTelecallerBankTeamleadOverview,
  fetchTelecallerBucketWiseAllocation,
  fetchTelecallerBucketWiseStatus,
} from "../../store/telecaller/bankDashboard";
import {
  formatCurrency,
  formatWithCommas,
  getUserType,
} from "../../assets/helpers";
import IntensityStrip from "../../Pages/DashboardPage/components/IntensityStrip";
import BarChart from "../../Reuseable/Barchart/Barchart";
import StackedBarChart from "../../Reuseable/StackedBar/StackedBarChart";
import { ReactComponent as Calender } from "../../assets/Icons/calendarIcon.svg";
import CustomSelect from "../../Reuseable/Select/Select";
import { fetchTeleCallers } from "../../store/userRoleSlice";
import { CloudDownloadOutlined, RightOutlined } from "@ant-design/icons";
import { downloadMontlyReport } from "../../assets/api/portfolio";
import toast from "react-hot-toast";

const BankDashboardComp = () => {
  const {
    bankOverview,
    bankOverviwLoading,
    case_intensity_counts,
    case_intensity_counts_loading,
    Monthly_ptp,
    Monthly_ptp_loading,
    Weekly_paid,
    Weekly_paid_loading,
    case_trend,
    case_trend_loading,
    allocation_breakdown_data,
    allocation_breakdown_loading,
  } = useSelector((state) => state?.portfolio);
  const { products_list, products_loading } = useSelector(
    (state) => state?.agency,
  );
  const { token } = useSelector((state) => state?.auth);
  const { telecallers, telecallers_loading } = useSelector(
    (state) => state?.userRoles,
  );
  const {
    bucketwise_allocation,
    bucketwise_loading,
    bucketwise_status_loading,
    bucketwise_status,
  } = useSelector((state) => state?.bucketwise);
  const { format } = useSelector((state) => state.priceFormat);
  const {
    telecaller_bucketwise_allocation,
    telecaller_bucketwise_allocation_loading,
    telecaller_bucketwise_status_loading,
    telecaller_bucketwise_status,
    telecaller_bank_teamlead_overview_loading,
    telecaller_bank_teamlead_overview,
    telecaller_bankreport_stats,
    telecaller_bankreport_stats_loading,
  } = useSelector((state) => state.telecallerBankDashboard);
  const { user } = useSelector((state) => state.auth);
  const [loadingBucketExport, setLoadingBucketExport] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [param, setParam] = useState({});

  useEffect(() => {
    dispatch(fetchBankOverview({ bank_id: id }));
    dispatch(fetchBucketWiseAllocation({ portfolio_id: id }));
    dispatch(fetchBucketWiseStatus({ portfolio_id: id }));
    dispatch(fetchTelecallerBucketWiseAllocation({ portfolio_id: id }));
    dispatch(fetchTelecallerBucketWiseStatus({ portfolio_id: id }));
    dispatch(fetchTelecallerBankTeamleadOverview({ portfolio_id: id }));
    dispatch(fetchTelecallerBankreportStats({ portfolio_id: id }));
    dispatch(fetchCaseIntencity({ portfolio: id }));
    dispatch(fetchCaseTrend(id));
    dispatch(fetchAllocationBreakdown(id));
    if (!user?.is_tc) {
      dispatch(fetchTeleCallers());
    }
  }, [dispatch, id, user]);


  const getAllocationData = () => {
    if (user?.is_tc) {
      return telecaller_bucketwise_allocation;
    } else {
      return bucketwise_allocation;
    }
  };
  const getStatusData = () => {
    if (user?.is_tc) {
      return telecaller_bucketwise_status;
    } else {
      return bucketwise_status;
    }
  };

  /* Read the slice data directly. The previous helper returned a fresh object
     literal every render, so using it as a memo dependency invalidated these
     on every render - which re-created the chart props and tore the chart down
     and re-initialised it on unrelated state changes such as ticking a
     product filter. */
  const overviewData = user?.is_tc
    ? telecaller_bank_teamlead_overview
    : bankOverview;
  const overviewLoading = user?.is_tc
    ? telecaller_bank_teamlead_overview_loading
    : bankOverviwLoading;

  const trendTableData = case_trend?.weeks || [];

  const trendColumns = [
    {
      title: "Week",
      dataIndex: "label",
      key: "label",
      render: (val) =>
        case_trend_loading ? <Skeleton.Input size="small" active /> : val,
    },
    {
      title: "Date",
      key: "date",
      render: (_, record) =>
        case_trend_loading ? (
          <Skeleton.Input size="small" active />
        ) : (
          `${record?.start_date || ""} to ${record?.end_date || ""}`
        ),
    },
    {
      title: "Total Cases",
      dataIndex: ["summary", "total_cases"],
      key: "total_cases",
      align: "right",
      render: (val) =>
        case_trend_loading ? (
          <Skeleton.Input size="small" active />
        ) : (
          formatWithCommas(val)
        ),
    },
    {
      title: "Total ODPOS",
      dataIndex: ["summary", "total_odpos"],
      key: "total_odpos",
      align: "right",
      render: (val) =>
        case_trend_loading ? (
          <Skeleton.Input size="small" active />
        ) : (
          formatCurrency(val, format)
        ),
    },
    {
      title: "Resolved ODPOS",
      dataIndex: ["summary", "resolved_odpos"],
      key: "resolved_odpos",
      align: "right",
      render: (val) =>
        case_trend_loading ? (
          <Skeleton.Input size="small" active />
        ) : (
          formatCurrency(val, format)
        ),
    },
    {
      title: "Unresolved ODPOS",
      dataIndex: ["summary", "unresolved_odpos"],
      key: "unresolved_odpos",
      align: "right",
      render: (val) =>
        case_trend_loading ? (
          <Skeleton.Input size="small" active />
        ) : (
          formatCurrency(val, format)
        ),
    },
    {
      title: "Resolved %",
      dataIndex: ["summary", "resolved_percent"],
      key: "resolved_percent",
      align: "right",
      render: (val) =>
        case_trend_loading ? (
          <Skeleton.Input size="small" active />
        ) : (
          `${val || 0}%`
        ),
    },
  ];

  const expandedRowRender = (record) => {
    const dailyColumns = [
      {
        title: "Day",
        key: "day",
        render: (_, dayRecord) => `${dayRecord.day_name || ""} ${dayRecord.date ? `(${dayRecord.date})` : ""}`,
      },
      {
        title: "Total Cases",
        dataIndex: ["cases", "total"],
        key: "total_cases",
        align: "right",
        render: (val) => formatWithCommas(val),
      },
      {
        title: "Total ODPOS",
        dataIndex: ["odpos", "total"],
        key: "total_odpos",
        align: "right",
        render: (val) => formatCurrency(val, format),
      },
      {
        title: "Resolved ODPOS",
        dataIndex: ["odpos", "resolved"],
        key: "resolved_odpos",
        align: "right",
        render: (val) => formatCurrency(val, format),
      },
      {
        title: "Unresolved ODPOS",
        dataIndex: ["odpos", "unresolved"],
        key: "unresolved_odpos",
        align: "right",
        render: (val) => formatCurrency(val, format),
      },
      {
        title: "Resolved %",
        dataIndex: ["odpos", "resolved_percent"],
        key: "resolved_percent",
        align: "right",
        render: (val) => `${val || 0}%`,
      },
    ];

    return (
      <Table
        columns={dailyColumns}
        dataSource={record.days || []}
        pagination={false}
        rowKey={(day, idx) => day.date || idx}
        size="small"
      />
    );
  };

  const barColors = useMemo(() => ["#4CB55C", "#F7A844"], []);

  const productOptions = products_list?.results?.map((item) => ({
    label: item?.products,
    value: item?.products,
  }));

  const [selectedProducts, setSelectedProducts] = useState([]);
  const [params, setParams] = useState({
    product: "",
    start_date: null,
    end_date: null,
  });
  const [monthlyReportParam, setMonthlyReportParam] = useState({});

  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const handleDateChange = (from, to) => {
    setFromDate(from);
    setToDate(to);

    // Handle clear case
    if (!from || !to) {
      setParams((prev) => ({
        ...prev,
        start_date: null,
        end_date: null,
      }));
      return;
    }

    setParams((prev) => ({
      ...prev,
      start_date: dayjs(from).format("DD-MM-YYYY"),
      end_date: dayjs(to).format("DD-MM-YYYY"),
    }));
  };

  const handleProductChange = useCallback((products) => {
    setSelectedProducts(products);
  }, []);

  useEffect(() => {
    dispatch(fetchAgencyProducts());
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchBankOverview({ bank_id: id, ...params }));
  }, [params])

  useEffect(() => {
    dispatch(fetchMonthlyPtp({ portfolio: id }));
    dispatch(fetchWeeklyPaid({ portfolio: id }));
  }, [dispatch, id]);

  const handleApplyMonthlyPtpFilter = () => {
    dispatch(fetchMonthlyPtp({ portfolio: id, ...monthlyReportParam }));
  };
  const handleExportMonthlyPtp = async () => {
    setLoading(true);
    try {
      const res = await downloadMontlyReport(token, {
        portfolio: id,
        export: true,
        ...monthlyReportParam,
      });
      if (res?.status === 200) {
        const url = res?.data?.data?.download_url;
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `Monthly_PTP_Report_${id}.xlsx`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setLoading(false);
      } else {
        setLoading(false);
        toast.error(res?.message || "Failed to export report");
      }
    } catch (error) {
      setLoading(false);
    }
  };
  console.log(loading, "lksdkjflfkdjfkldjklf");

  const handleApplyFilter = (products) => {
    setParams((prev) => ({
      ...prev,
      products,
    }));
  };

  const days = [
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "Sunday",
  ];

  // console.log(Monthly_ptp?.callers,'lkjdflkdjflkdjf');

  const tableData = useMemo(() => {
    if (!Monthly_ptp?.callers) return [];

    return Object.entries(Monthly_ptp.callers).map(([name, stats]) => ({
      key: name,
      telecaller: name,
      ...(stats || {}),
    }));
  }, [Monthly_ptp?.callers]);

  const weeklyPaidTableData = useMemo(() => {
    if (!Weekly_paid?.callers) return [];

    return Object.entries(Weekly_paid.callers).map(([name, stats]) => ({
      key: name,
      telecaller: name,
      ...(stats || {}),
    }));
  }, [Weekly_paid?.callers]);

  const columns = [
    {
      title: "Telecaller",
      dataIndex: "telecaller",
      key: "telecaller",
      fixed: "left",
      width: 150,
      render: (val) =>
        Monthly_ptp_loading ? <Skeleton.Input size="small" active /> : val,
    },
    {
      title: "Past Weeks Count",
      dataIndex: "total_past_weeks",
      key: "total",
      render: (value) =>
        Monthly_ptp_loading ? (
          <Skeleton.Input size="small" active />
        ) : value ? (
          formatCurrency(value.ptp_amount, format)
        ) : (
          "-"
        ),
    },

    ...days.map((day) => ({
      title: day,
      dataIndex: day,
      key: day,
      render: (value) =>
        Monthly_ptp_loading ? (
          <Skeleton.Input size="small" active />
        ) : value ? (
          formatCurrency(value.ptp_amount, format)
        ) : (
          "-"
        ),
    })),
  ];
  const columns2 = [
    {
      title: "Telecaller",
      dataIndex: "telecaller",
      key: "telecaller",
      fixed: "left",
      width: 150,
      render: (val) =>
        Weekly_paid_loading ? <Skeleton.Input size="small" active /> : val,
    },
    {
      title: "Past Weeks Count",
      dataIndex: "total_past_weeks",
      key: "total",
      render: (value) =>
        Weekly_paid_loading ? (
          <Skeleton.Input size="small" active />
        ) : value ? (
          formatCurrency(value.paid_amount, format)
        ) : (
          "-"
        ),
    },

    ...days.map((day) => ({
      title: day,
      dataIndex: day,
      key: day,
      render: (value) =>
        Weekly_paid_loading ? (
          <Skeleton.Input size="small" active />
        ) : value ? (
          formatCurrency(value.paid_amount, format)
        ) : (
          "-"
        ),
    })),
  ];

  const handleBucketExport = async () => {
    try {
      setLoadingBucketExport(true);
      const res = await exportBucketAllocationFile(id, token);
      if (res?.status === 200) {
        const link = document.createElement("a");
        link.href = res?.data?.data?.download_url;
        link.setAttribute("download", `bucketwise_allocation_${id}.xlsx`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } finally {
      setLoadingBucketExport(false);
    }
  };

  const onWeekPTPChange = (date) => {
    if (date) {
      dispatch(
        fetchMonthlyPtp({
          portfolio: id,
          week: dayjs(date).week(),
          year: dayjs(date).year(),
        }),
      );
    }
    if (!date) {
      dispatch(
        fetchMonthlyPtp({
          portfolio: id,
          // week: dayjs(date).week(),
          // year: dayjs(date).year()
        }),
      );
    }
  };
  const onWeekPaidChange = (date) => {
    if (date) {
      dispatch(
        fetchWeeklyPaid({
          portfolio: id,
          week: dayjs(date).week(),
          year: dayjs(date).year(),
        }),
      );
    }
    if (!date) {
      dispatch(
        fetchWeeklyPaid({
          portfolio: id,
          // week: dayjs(date).week(),
          // year: dayjs(date).year()
        }),
      );
    }
  };

  return (
    <Flex className="bankDashboardCompMain">
      {/* Summary row. Telecallers get their own stats endpoint; everyone else
          reads the same figures off the bank overview payload. */}
      {(() => {
        const kpi = user?.is_tc
          ? telecaller_bankreport_stats
          : bankOverview?.donut_chart;
        const kpiLoading = user?.is_tc
          ? telecaller_bankreport_stats_loading
          : bankOverviwLoading;
        if (!kpi && !kpiLoading) return null;
        return (
          <div className="dsh-kpi-grid">
            <KpiCard
              title="Total Allocated"
              icon={<DatabaseOutlined />}
              accent="brand"
              loading={kpiLoading}
              value={formatWithCommas(kpi?.total_allocated)}
            />

            <KpiCard
              title="Telecaller & Field Agent"
              icon={<UserSwitchOutlined />}
              accent="blue"
              variant="split"
              loading={kpiLoading}
              value={formatWithCommas(kpi?.total_allocated)}
              segments={[
                { name: "Telecaller", value: kpi?.telecaller_percent },
                { name: "Field Agent", value: kpi?.field_agent_percent },
              ]}
            />

            <KpiCard
              title="EMI Overdue"
              icon={<DollarOutlined />}
              accent="amber"
              loading={kpiLoading}
              value={formatCurrency(kpi?.emi_overdue, format)}
              footerLabel="EMI Collected"
              footerValue={formatCurrency(kpi?.emi_collected, format)}
            />
          </div>
        );
      })()}

      {(user?.is_tc || user?.is_agency || user?.is_supervisor) && (
        <IntensityStrip
          title="Intensity Counts"
          subtitle={null}
          items={case_intensity_counts?.intensity || []}
          loading={case_intensity_counts_loading}
          onSelect={(item) => {
            dispatch(setQuery({ intensity: item?.intensity, portfolio: id }));
            navigate("/allocation/cases");
          }}
        />
      )}
      <PanelCard
        title="Weekly PTP"
        icon={<TableOutlined />}
        accent="blue"
        padded={false}
        actions={
          <DatePicker
            className="dsh-datepicker"
            picker="week"
            format="YYYY-ww"
            onChange={onWeekPTPChange}
          />
        }
      >
        <div className="bank-report-table">
          <Table
            columns={columns}
            dataSource={Monthly_ptp_loading ? Array(5).fill({}) : tableData}
            rowKey={(record, index) => record?.key ?? index}
            pagination={false}
            scroll={{ x: "max-content" }}
          />
        </div>
      </PanelCard>
      <PanelCard
        title="Weekly Paid"
        icon={<TableOutlined />}
        accent="green"
        padded={false}
        actions={
          <DatePicker
            className="dsh-datepicker"
            picker="week"
            format="YYYY-ww"
            onChange={onWeekPaidChange}
          />
        }
      >
        <div className="bank-report-table">
          <Table
            columns={columns2}
            dataSource={
              Weekly_paid_loading ? Array(5).fill({}) : weeklyPaidTableData
            }
            rowKey={(record, index) => record?.key ?? index}
            pagination={false}
            scroll={{ x: "max-content" }}
          />
        </div>
      </PanelCard>

      <PanelCard
          title="Bucketwise Trend"
          icon={<TableOutlined />}
          accent="brand"
          padded={false}
        >
          <div className="bank-report-table">
            <Table
              columns={trendColumns}
              dataSource={
                case_trend_loading ? Array(5).fill({}) : trendTableData
              }
              rowKey={(record, index) => record?.week_number ?? index}
              pagination={false}
              scroll={{ x: "max-content" }}
              expandable={{
                expandedRowRender,
                rowExpandable: (record) => record.days && record.days.length > 0,
                expandIcon: ({ expanded, onExpand, record }) =>
                  record.days && record.days.length > 0 ? (
                    <RightOutlined
                      onClick={(e) => onExpand(record, e)}
                      style={{
                        cursor: "pointer",
                        transition: "transform 0.3s ease",
                        transform: expanded ? "rotate(90deg)" : "none",
                      }}
                    />
                  ) : null,
              }}
            />
          </div>
        </PanelCard>

        {user?.is_tc ? null : (
          <PanelCard
            title="Teamlead Report Overview"
            icon={<PieChartOutlined />}
            accent="green"
            actions={
              <Flex className="graph-filter">
                <CheckboxFilterDropdown
                  label="Products"
                  options={productOptions}
                  selected={selectedProducts}
                  onChange={handleProductChange}
                  handleApplyFilter={handleApplyFilter}
                />
                <DateFilter onDateChange={handleDateChange} />
              </Flex>
            }
          >
            <AllocationBreakdownOverview
              data={allocation_breakdown_data}
              loading={allocation_breakdown_loading}
              format={format}
            />
          </PanelCard>
        )}

      <PanelCard
        title="Bucketwise Allocation"
        icon={<TableOutlined />}
        accent="brand"
        padded={false}
        actions={
          <NormalButton
            type="primary"
            loading={loadingBucketExport}
            suffixIcon={<ExportICon />}
            onClick={handleBucketExport}
          >
            Export
          </NormalButton>
        }
      >
        <div className="bank-report-table">
          <BucketwiseTable
            data={getAllocationData()?.data || []}
            loading={
              bucketwise_loading || telecaller_bucketwise_allocation_loading
            }
            id={id}
          />
        </div>
      </PanelCard>

      <PanelCard
        title="Bucket Status"
        icon={<TableOutlined />}
        accent="violet"
        padded={false}
      >
        <div className="bank-report-table">
          <BucketStatusTable
            data={getStatusData()?.classification_data}
            loading={
              bucketwise_status_loading || telecaller_bucketwise_status_loading
            }
          />
        </div>
      </PanelCard>
    </Flex>
  );
};
export default BankDashboardComp;
